import type { SVGProps } from "react";
import Chip from "@mui/material/Chip";
import {
  HiCash,
  HiCreditCard,
  HiReceiptTax,
  HiRefresh,
  HiSwitchHorizontal,
} from "react-icons/hi";
import { FaBarcode, FaMoneyCheckAlt } from "react-icons/fa";
import { colors } from "../data/tokens";

// Ícone oficial do Pix (Font Awesome Free v7.3.1 — fontawesome.com/license/free).
// Inline porque a versão instalada do react-icons não o inclui;
// currentColor herda a cor do chip. Exportado para reuso (KPIs etc.).
export const PixIcon = ({
  size = 13,
  ...rest
}: { size?: number } & SVGProps<SVGSVGElement>) => (
  // O spread vem primeiro e ANTES dos nossos atributos: o Chip do MUI
  // clona o ícone injetando className (MuiChip-icon) — sem repassá-la,
  // a margem e a cor do chip nunca chegam ao svg
  <svg
    {...rest}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 640 640"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M306.4 356.5C311.8 351.1 321.1 351.1 326.5 356.5L403.5 433.5C417.7 447.7 436.6 455.5 456.6 455.5L471.7 455.5L374.6 552.6C344.3 582.1 295.1 582.1 264.8 552.6L167.3 455.2L176.6 455.2C196.6 455.2 215.5 447.4 229.7 433.2L306.4 356.5zM326.5 282.9C320.1 288.4 311.9 288.5 306.4 282.9L229.7 206.2C215.5 191.1 196.6 184.2 176.6 184.2L167.3 184.2L264.7 86.8C295.1 56.5 344.3 56.5 374.6 86.8L471.8 183.9L456.6 183.9C436.6 183.9 417.7 191.7 403.5 205.9L326.5 282.9zM176.6 206.7C190.4 206.7 203.1 212.3 213.7 222.1L290.4 298.8C297.6 305.1 307 309.6 316.5 309.6C325.9 309.6 335.3 305.1 342.5 298.8L419.5 221.8C429.3 212.1 442.8 206.5 456.6 206.5L494.3 206.5L552.6 264.8C582.9 295.1 582.9 344.3 552.6 374.6L494.3 432.9L456.6 432.9C442.8 432.9 429.3 427.3 419.5 417.5L342.5 340.5C328.6 326.6 304.3 326.6 290.4 340.6L213.7 417.2C203.1 427 190.4 432.6 176.6 432.6L144.8 432.6L86.8 374.6C56.5 344.3 56.5 295.1 86.8 264.8L144.8 206.7L176.6 206.7z" />
  </svg>
);

// ------------------------------------------------------------------
// Badge global de forma de pagamento (Transações e onde mais precisar):
// família detectada por palavra-chave SEM acento — resiliente às
// variantes do legado ("Boleto Bancário", "(IUGU)", gravações
// corrompidas). Cor da paleta do tema (tokens) + ícone por família;
// o rótulo exibido é sempre o valor real gravado no registro.
// ------------------------------------------------------------------

type ChipColor = "primary" | "success" | "warning" | "default";

type Family = {
  color: ChipColor;
  icon: JSX.Element;
  // Cor fora da paleta do MUI (ex.: verde oficial do Pix) — aplicada
  // via sx em texto, borda e ícone
  custom?: string;
};

const strip = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const family = (method: string): Family => {
  const m = strip(method);
  if (m.includes("boleto"))
    return { color: "primary", icon: <FaBarcode size={12} /> };
  if (m.includes("pix"))
    return {
      color: "default",
      custom: colors.pix,
      icon: <PixIcon size={13} />,
    };
  if (m.includes("transf"))
    return { color: "success", icon: <HiSwitchHorizontal size={14} /> };
  if (m.includes("dinheiro") || m.includes("deposito"))
    return { color: "success", icon: <HiCash size={14} /> };
  if (m.includes("cart"))
    return { color: "warning", icon: <HiCreditCard size={14} /> };
  if (m.includes("cheque"))
    return { color: "warning", icon: <FaMoneyCheckAlt size={12} /> };
  if (m.includes("isen"))
    return { color: "default", icon: <HiReceiptTax size={14} /> };
  if (m.includes("reativa") || m.includes("ajuste"))
    return { color: "default", icon: <HiRefresh size={14} /> };
  return { color: "default", icon: <HiCash size={14} /> };
};

export const PaymentMethodChip = ({
  method,
}: {
  method?: string | null;
}) => {
  if (!method || !String(method).trim()) return null;
  const { color, icon, custom } = family(String(method));
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      icon={icon}
      label={method}
      sx={{
        // Respiro do ícone em relação à borda esquerda do chip (os SVGs
        // do Font Awesome desenham até a beirada do viewBox)
        "& .MuiChip-icon": { marginLeft: "8px" },
        ...(custom
          ? {
              color: custom,
              borderColor: custom,
              "& .MuiChip-icon": { marginLeft: "8px", color: custom },
            }
          : {}),
      }}
    />
  );
};
